import React from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { canRouteTo, firstPermittedPath, viewerFromAuth } from "../enums/Navigation";
import AccessDeniedScreen from "../componenets/common/AccessDeniedScreen";

/**
 * Keeps a user off a routed screen that is not theirs.
 *
 * <p>WHY THIS EXISTS: {@link AuthGuard} answers exactly one question — is anyone logged in? Nothing
 * else stood between a URL and a page, so any authenticated staff member who typed
 * {@code /app/exercises} (or followed a stale link, or hit browser autocomplete) got the screen. It
 * rendered, mounted its sections, and every request they fired came back 403 — a half-drawn page
 * and, at best, the app-wide refusal screen. The nav had known all along that the screen was not
 * theirs; routing simply never asked. Now both ask the same registry
 * ({@code src/enums/Navigation.ts}).
 *
 * <p>Redirecting to the user's own first screen — rather than showing a refusal — is deliberate:
 * arriving somewhere you were never offered is almost always a stale URL, not an attempt, and the
 * useful response is to put the person somewhere that works.
 *
 * <p><b>THIS IS PRESENTATION, NOT ENFORCEMENT.</b> The server is the security boundary and decides
 * every request independently from the caller's own principal; this guard cannot grant anything and
 * must never be relied on as a control. If it gets a rule wrong in the permissive direction, the
 * request still 403s and the app-wide {@code AccessDeniedScreen} ({@code AccessDeniedContext}) is
 * the safety net that catches it.
 */
export const PermissionGuard = ({ children }: { children?: ReactNode }) => {
  const auth = useAuth();
  const location = useLocation();

  // No identity yet (still loading, or rendered outside AuthProvider): AuthGuard owns the
  // logged-out redirect, and guessing here would flash a redirect at a user who is merely early.
  if (!auth?.currentUser) return <>{children}</>;

  const viewer = viewerFromAuth(auth);
  if (canRouteTo(location.pathname, viewer)) return <>{children}</>;

  const fallback = firstPermittedPath(viewer);
  // Nothing at all is permitted (login already rejects anyone outside ADMIN_APP_ROLES, so this
  // means the registry and the login gate disagree), or the fallback is the very screen we just
  // refused. Either way a <Navigate> would loop — say no instead.
  if (!fallback || fallback === location.pathname) return <AccessDeniedScreen />;

  return <Navigate to={fallback} replace />;
};

export default PermissionGuard;
